import { DurableProductReviewPanel } from '../../app/development/DurableProductReviewPanel';
import { LayoutFrame } from '../../app/LayoutFrame';
import { NonOperationalBanner } from '../../app/NonOperationalBanner';
import { createLiveCapabilityDefinitionAdapter } from '../../features/capability-equipment/definition/adapters/live-capability-definition-adapter';
import { CapabilityDefinitionLivePanel } from '../../features/capability-equipment/definition/ui/CapabilityDefinitionLivePanel';
import { CapabilityEquipmentCenter } from '../../features/capability-equipment/ui/CapabilityEquipmentCenter';
import { createLiveMissionTemplateAdapter } from '../../features/mission-workspace/template/adapters/live-mission-template-adapter';
import { MissionTemplateLivePanel } from '../../features/mission-workspace/template/ui/MissionTemplateLivePanel';
import { MissionWorkspacePage } from '../../features/mission-workspace/ui/MissionWorkspacePanel';
import { Page01 } from '../../features/overview/ui/OverviewPage';
import { CommandWorkspacePage } from '../../features/risk-warning/ui/command-workspace/CommandWorkspacePage';
import { FireSignalsPage } from '../../features/risk-warning/ui/fire-signals/FireSignalsPage';
import { ExternalPage } from '../../features/risk-warning/ui/historical/ExternalPage';
import { HistoricalPage } from '../../features/risk-warning/ui/historical/HistoricalPage';
import { NormalMonitoringPage } from '../../features/risk-warning/ui/NormalMonitoringPage';
import { OverviewMonitoringSummary } from '../../features/risk-warning/ui/OverviewSummary';
import { ScenarioCenterPage } from '../../features/scenario-center/ui/ScenarioCenterPage';
import {
  apiBaseOrigin,
  configuredApiOrigin,
  syntheticDemoPortalsEnabled,
} from '../../shared/config/api-origin';
import { isOverviewPath, platformRoutes } from '../../shared/platform/platform-routes';
import { currentPortal } from '../../shared/platform/portal-context';
import { resolvePortalRoute } from '../../shared/platform/portal-routes';
import { createLiveProductReviewAdapter } from '../../shared/product-review/adapters/live-product-review-adapter';
import { GovernancePortal } from '../admin/GovernancePortal';
import { ProviderPortal } from '../vendor/ProviderPortal';

function DevelopmentMissionTemplate() {
  const port = createLiveMissionTemplateAdapter({ baseOrigin: apiBaseOrigin() });
  return <MissionTemplateLivePanel port={port} />;
}

function DevelopmentCapabilityEquipment() {
  const port = createLiveCapabilityDefinitionAdapter({ baseOrigin: apiBaseOrigin() });
  return <CapabilityEquipmentCenter port={port} />;
}

function DevelopmentCapabilityDefinition() {
  // Evaluated only when the development component is rendered in a browser.
  const port = createLiveCapabilityDefinitionAdapter({ baseOrigin: apiBaseOrigin() });
  return <CapabilityDefinitionLivePanel port={port} />;
}

/**
 * The P2-BE-SB02 durable 06/08 chain.
 *
 * IT IS BUILT FROM `import.meta.env`, NOT FROM A HARD-CODED ORIGIN. The same-origin requirement is
 * the repository's: the vite config deliberately carries no `server.proxy`, because a proxy entry
 * pointing at a backend would be a real transport binding. This page therefore assumes it is served
 * from the SAME origin as the Boot host — which is what `tools/db/serve-durable-slice.mjs` arranges
 * for a local run, and what the platform publisher would have to arrange for a staged one.
 *
 * The session tokens are synthetic fixtures with synthetic defaults. They are NOT secrets, and the
 * `SYNTHETIC-` prefix is there so nobody mistakes one for a leaked credential.
 */
function DevelopmentDurableProductReview() {
  const port = createLiveProductReviewAdapter({ baseOrigin: apiBaseOrigin() });
  return (
    <DurableProductReviewPanel
      port={port}
      defaultProductId={`SYN-UI-${Date.now()}`}
      defaults={{
        country: import.meta.env.VITE_RESCUE_SYN_COUNTRY ?? 'SYN-COUNTRY',
        supplier: import.meta.env.VITE_RESCUE_SYN_SUPPLIER ?? 'SYN-SUPPLIER',
        reviewScope: import.meta.env.VITE_RESCUE_SYN_REVIEW_SCOPE ?? 'SYN-REVIEW',
        sessionSupplier:
          import.meta.env.VITE_RESCUE_SESSION_SUPPLIER ?? 'SYNTHETIC-SESSION-SUPPLIER',
        sessionReviewer:
          import.meta.env.VITE_RESCUE_SESSION_REVIEWER ?? 'SYNTHETIC-SESSION-REVIEWER',
        sessionOwner: import.meta.env.VITE_RESCUE_SESSION_OWNER ?? 'SYNTHETIC-SESSION-OWNER',
      }}
    />
  );
}

/** Firefighter center composition; legacy unbound DEV routes remain compatible. */
export function FirefighterApp({ pathname }: { readonly pathname: string }) {
  const portal = currentPortal();
  const developmentBrowser = import.meta.env.DEV && typeof window !== 'undefined';
  const portalRoute =
    typeof window === 'undefined'
      ? null
      : resolvePortalRoute(pathname, syntheticDemoPortalsEnabled());
  const scenarioCenter =
    typeof window !== 'undefined' && pathname.replace(/\/$/, '') === platformRoutes.scenario;
  const scenarioCenterSyntheticRead =
    developmentBrowser &&
    new URLSearchParams(window.location.search).get('scenarioCenterSyntheticRead') === '1';
  const capabilityEquipment =
    typeof window !== 'undefined' && pathname === platformRoutes.equipment;
  const page01 = typeof window !== 'undefined' && isOverviewPath(pathname);
  const monitoring =
    typeof window !== 'undefined' &&
    /^\/normal-operations\/risk-warning(?:\/|$)/.test(window.location.pathname);
  const commandWorkspace =
    typeof window !== 'undefined' &&
    window.location.pathname.replace(/\/$/, '') === '/normal-operations/risk-warning' &&
    new URLSearchParams(window.location.search).get('view') !== 'records';
  const external =
    typeof window !== 'undefined' &&
    /^\/normal-operations\/risk-warning\/evaluation\/historical\/external(?:\/|$)/.test(
      window.location.pathname,
    );
  const fireSignals =
    typeof window !== 'undefined' &&
    /^\/normal-operations\/risk-warning\/fire-signals(?:\/|$)/.test(window.location.pathname);
  const historical =
    typeof window !== 'undefined' &&
    /^\/normal-operations\/risk-warning\/evaluation\/historical(?:\/|$)/.test(
      window.location.pathname,
    );
  const workspace =
    typeof window !== 'undefined' && pathname.replace(/\/$/, '') === platformRoutes.workspace;
  const durableReview =
    typeof window !== 'undefined' && pathname.replace(/\/$/, '') === platformRoutes.durableReview;
  return (
    <>
      {' '}
      {portalRoute && portal === 'firefighter' ? (
        <LayoutFrame>
          <h1>请使用对应端的独立入口</h1>
          <p>厂商端：5181 · 管理员端：5182</p>
        </LayoutFrame>
      ) : portalRoute === 'provider' ? (
        <ProviderPortal />
      ) : portalRoute === 'governance' ? (
        <GovernancePortal />
      ) : portalRoute === 'blocked' ? (
        <LayoutFrame>
          <section aria-label="演示门户访问限制">
            <h1>演示门户尚未开放</h1>
            <p>请在开发环境或明确启用的合成演示环境中访问。</p>
            <a href="/demo/page-01">返回综合态势</a>
          </section>
        </LayoutFrame>
      ) : fireSignals ? (
        <FireSignalsPage />
      ) : external ? (
        <ExternalPage />
      ) : historical ? (
        <HistoricalPage />
      ) : scenarioCenter ? (
        <ScenarioCenterPage
          {...(scenarioCenterSyntheticRead || developmentBrowser || configuredApiOrigin()
            ? { developmentLoopbackOrigin: apiBaseOrigin() }
            : {})}
        />
      ) : commandWorkspace ? (
        <CommandWorkspacePage />
      ) : monitoring ? (
        <NormalMonitoringPage />
      ) : workspace ? (
        <MissionWorkspacePage />
      ) : capabilityEquipment ? (
        developmentBrowser ? (
          <DevelopmentCapabilityEquipment />
        ) : (
          <CapabilityEquipmentCenter />
        )
      ) : page01 ? (
        <Page01 monitoringSummary={<OverviewMonitoringSummary />} />
      ) : durableReview ? (
        developmentBrowser ? (
          <LayoutFrame>
            <DevelopmentDurableProductReview />
          </LayoutFrame>
        ) : (
          <LayoutFrame>
            <NonOperationalBanner />
            <h1>该页面仅在开发模式开放</h1>
            <p>
              合成管理写入面没有正式管理授权；它在开发模式之外不渲染，服务端也会在
              <code>rescue.persistence.enabled</code> 未显式开启时根本不注册这些路由。
            </p>
          </LayoutFrame>
        )
      ) : pathname.replace(/\/$/, '') === platformRoutes.queries ? (
        <LayoutFrame>
          <a href={platformRoutes.overview}>返回主平台</a>
          {developmentBrowser ? (
            <section className="fe1w0-banner" aria-label="本地验证声明">
              <h2 className="fe1w0-banner__heading">合成数据 / 非运营</h2>
              <p className="fe1w0-banner__body">
                此开发态查询只读取本地测试服务的合成能力定义与
                MissionTemplate，不连接生产数据、不控制设备。
              </p>
              <p className="fe1w0-banner__body">查询结果不构成行动授权。</p>
            </section>
          ) : (
            <NonOperationalBanner />
          )}
          <p>
            <a href="/demo/capability-equipment">能力与装备中心</a>
          </p>
          <p>
            <a href="/demo/scenario-center">场景中心</a>
          </p>
          {developmentBrowser && <DevelopmentCapabilityDefinition />}
          {developmentBrowser && <DevelopmentMissionTemplate />}
        </LayoutFrame>
      ) : (
        <LayoutFrame>
          <h1>页面不存在</h1>
          <p>请从主平台导航选择功能区。</p>
          <a href={platformRoutes.overview}>返回主平台</a>
        </LayoutFrame>
      )}{' '}
    </>
  );
}
